/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */

import {Component, EventEmitter, Input} from '@angular/core';

/**
 * Confirm modal, similar in spirit as the browser 'window.confirm' method. Presents the user with a confirm dialog
 * with a confirm and cancel buttons.
 *
 * Typically used directly from the modal service using :
 *
 * ```
 * this.modalService.confirm({
 *   title: 'My confirm title',
 *   text: 'My confirm text'
 * });
 * ```
 */
@Component({
  selector: 'gp-modal-confirm',
  template: `
    <div class="modal in" style="display: block;">
      <div class="modal-dialog"
           [ngClass]="{
           customClass: customClass,
           'modal-lg': size === 'lg',
           'modal-md': size === 'md',
           'modal-sm': size === 'sm'
           }">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i *ngIf="type" [ngClass]="getIconClass()" aria-hidden="true"></i>
              {{ title | translate }}
            </h5>
          </div>
          <div class="modal-body p-lg">
            <div [innerHTML]="text | translate"></div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn p-x-md" [ngClass]="getConfirmButtonClass()" test-id="confirmButton" (click)="ok()">
              {{ 'FORMS.confirm' | translate }}
            </button>
            <button type="button" class="btn btn-secondary p-x-md" test-id="cancelButton" (click)="cancel()">
              {{ 'FORMS.cancel' | translate }}
            </button>
          </div>
        </div>
      </div>
    </div>
  `,
  styleUrls: ['./confirm-modal.component.scss']
})
export class ConfirmModalComponent {

  /**
   * @type {string} One of 'lg', ...
   */
  @Input()
  public size: string = '';

  /**
   * @type {string} Custom css class for this modal
   */
  @Input()
  public customClass = '';

  /**
   * @type {string} Title of the confirm modal
   */
  @Input()
  public title: string = 'Confirm';

  /**
   * @type {string} Body text of the confirm modal
   */
  @Input()
  public text: string = '';

  /**
   * Optional severity of the confirmation.
   * - 'danger'  -> shows a danger icon before the title and a red confirm button
   * - 'warning' -> shows a warning icon before the title (confirm button stays black)
   * - undefined -> no icon, black confirm button (default behaviour)
   */
  @Input()
  public type: 'danger' | 'warning';

  /**
   * @type {EventEmitter<boolean>} Result of the confirm dialog depending on user interaction
   */
  public result: EventEmitter<boolean> = new EventEmitter<boolean>();

  /**
   * Returns the icon css class shown before the title depending on the type.
   */
  public getIconClass(): string {
    if (this.type === 'danger') {
      return 'fa-duotone fa-diamond-exclamation p-r-sm';
    }
    if (this.type === 'warning') {
      return 'fa-duotone fa-triangle-exclamation p-r-sm';
    }
    return '';
  }

  /**
   * Returns the css class for the confirm button.
   * Red (danger) when the type is 'danger', otherwise black.
   */
  public getConfirmButtonClass(): string {
    return this.type === 'danger' ? 'btn-danger' : 'btn-dark';
  }

  /**
   * Confirm
   */
  public ok() {
    this.result.emit(true);
    this.result.complete();
  }

  /**
   * Cancel
   */
  public cancel() {
    this.result.emit(false);
    this.result.complete();
  }
}
